import React, { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router'
import { Link } from 'react-router-dom'
import moment from 'moment'
import { getEmailTemplateInfo } from '../resources/api-constants'
import { ROUTES } from '../resources/other-constants'
import { SystemConfig, UserAppointment } from '../types/services'
import CustomAxios from '../utility/customAxios'
import './../styles/TemplateMail.sass'

const defaultTemplateConfig: SystemConfig = {
    settings: {
        footer: '',
        contacts: '',
        headerImg: '',
        promo: '',
        promoLink: ''
    },
    idOffice: 0
}

const RecapPage: React.FC = () => {
    const [headquarterLabel, setHeadquarterLabel] = useState('')
    const [appointmentEventData, setAppointmentEventData] = useState<UserAppointment | null>(null)
    const [mailTemplateData, setMailTemplateData] = useState<SystemConfig>(defaultTemplateConfig)
    const { state } = useLocation()
    const navigate = useNavigate()

    /**
     * Recupera i dati del template della mail di riepilogo dal database
     */
    const getConfigData = async (id: string) => {
        try {
            const templateElements: SystemConfig[] = await CustomAxios.get(getEmailTemplateInfo()).then((response) => response.data.items)
            const filtered = templateElements.find((element: SystemConfig) => element.idOffice === parseInt(id, 10)) || defaultTemplateConfig
            setMailTemplateData(filtered)
        } catch (error) {
            console.error(error)
        }
    }

    /**
     * Al render di questa pagina controlla che siano stati passati i dati della
     * prenotazione, altrimenti l'utente viene reindirizzato alla homepage.
     * Se invece i dati esistono vengono salvati nell'apposita state.
     */
    useEffect(() => {
        if (state === null) {
            navigate(ROUTES.HOMEPAGE_ROUTE)
        } else if (state !== null && state.eventData) {
            setAppointmentEventData(state.eventData)
            console.log(state.eventData)
        }
    }, [state, navigate])

    /**
     * Quando i dati dell'appuntamento sono disponibili, imposta il label della sede e carica i dati del template
     */
    useEffect(() => {
        if (appointmentEventData?.office?.parentOffice) {
            setHeadquarterLabel(appointmentEventData.office.parentOffice.name)
            getConfigData(appointmentEventData.office.parentOffice.id.toString())
        }
    }, [appointmentEventData])

    /**
     * Crea una stringa per ricapitolare all'utente l'esito della creazione dell'appuntamento, con
     * tanto di dati sul servizio, ufficio, operatore, giorno e ora precedentemente scelti.
     * @returns Messaggio con dati formattati
     */
    const getMainRecapText = useMemo(() => {
        if (appointmentEventData !== null) {
            const appointmentDate = moment(appointmentEventData.startsOn)
            return (
                <>
                    <h3>
                        Ufficio: {appointmentEventData.office?.name || 'N/A'} - {appointmentEventData.service?.name || 'N/A'}
                    </h3>
                    <h3>
                        {appointmentDate.format('DD/MM/YYYY')} - Ore {appointmentDate.format('HH:mm')}
                    </h3>
                    {appointmentEventData.office?.parentOffice && (
                        <h3>{appointmentEventData.office.parentOffice.address}</h3>
                    )}
                </>
            )
        }
        return ''
    }, [appointmentEventData])

    // Example value of appointmentEventData object

    return (
        <>
            {/** <div className='page-header-section'>
                <div className="main-section-title">Riepilogo prenotazione</div>
                <div className="main-logo-container print-only">
                    <img src={mainLogo} className="App-logo" alt="logo" />
                </div>
            </div>
            */}
            <div className="mail-main-content">
                <div className="template-header">
                    <div className="template-header-image-container">
                        <img alt="Camera di commercio Toscana Nord Ovest" src={mailTemplateData.settings.headerImg} />
                    </div>
                    <div className="template-header-headquarter-container">{headquarterLabel || ''}</div>
                </div>
                <h2>Abbiamo ricevuto la tua richiesta di appuntamento</h2>
                <p className="subtitle">
                    Abbiamo preso in carico la tua richiesta per fissare un appuntamento. A breve riceverai una mail con la conferma e il riepilogo dei dati ad
                    esso associati.
                </p>
                <p className="subtitle centered-text">Dati appuntamento:</p>
                <div className="recap-text-message">{getMainRecapText}</div>
                <p className="subtitle">{mailTemplateData.settings.promo}</p>
                <a href={mailTemplateData.settings.promoLink} className="subtitle template-link">
                    {mailTemplateData.settings.promoLink}
                </a>
                <div className="template-footer">
                    <p className="subtitle">{mailTemplateData.settings.footer}</p>
                </div>
                <div className="buttons-actions-list medium-padding-bottom-container">
                    <button className="primary-button noprint" onClick={() => window.print()}>
                        Stampa
                    </button>
                    <Link to={ROUTES.HOMEPAGE_ROUTE} className="primary-button noprint">
                        Nuova prenotazione
                    </Link>
                </div>
            </div>
        </>
    )
}

export default RecapPage
